import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import useStore from 'Hooks/useStore';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { formatStandardTimeParts, usesTwelveHourClock } from 'Util/_HumanWritten/TimeUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { classColour } from 'Modules/Ferries/Utils/seatGrid';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button } from 'Views/Components/Button/Button';
import WaitlistButton from 'Modules/MultiStopWizard/Components/Leg/WaitlistButton';
import { useLegOptionClick } from 'Modules/MultiStopWizard/Hooks/useLegOptionClick';
import { useIsLegSelectable } from 'Modules/MultiStopWizard/Hooks/useIsLegSelectable';
import { legClash } from 'Modules/MultiStopWizard/Hooks/legClash';
import { isWalkOnNotOffered, walkOnNotOfferedMessage } from 'Modules/MultiStopWizard/Hooks/walkOnNotOffered';
import { useLegErrorMessage } from 'Modules/MultiStopWizard/Hooks/useLegErrorMessage';
import {
	BookingWizardData,
	fareFieldLeg,
	passengerCountsByType,
	passengerFaresByLeg,
	patchAllPassengers,
} from '../../BookingWizardData/BaseBookingWizardData';
import { classPartyTotal, splitPartyTotal } from './tripClassFares';
import { formatFareChange } from './fareChange';
import { capacityLabel, classCapacityTag, fareAvailability, sailingIsGone } from './fareAvailability';
import { selectedClassId } from './TripFareColumns';
import { passengerClassPatch } from '../../BookingWizardData/PassengerDetail';
import { describeClassPoints } from '../Seating/seatingLayout';
import ClassInfoPopover from './ClassInfoPopover';

/**
 * What the dot beside a class name is filled with on a phone.
 *
 * <p>The same three colours the trip cards use for a sailing's own availability, so green, amber
 * and grey mean here what they mean everywhere else. It replaces the class's own colour at that
 * size deliberately: a booker reading a stack of full-width rows is asking "can my party get on
 * this one", and the class already has its name in front of them — the colour was telling them
 * something they could read.</p>
 */
const availabilitySwatch = (availability: string) => {
	if (availability === 'SOLD_OUT') {
		return '#c4ccd2';
	}
	return availability === 'AVAILABLE'
		? 'var(--high-availability-trip-card-colour, #16a34a)'
		: 'var(--low-availability-trip-card-colour, #e0a137)';
};

/**
 * A sailing, priced per fare class.
 *
 * <p><b>Its own card rather than a row bolted under <c>LegOption</c>.</b> That is what the first
 * attempt did, and the columns could never line up: <c>LegOption</c> is a flex band whose journey
 * block is <c>flex: 1</c>, so the white area starts wherever the times happen to end — a different
 * place on every sailing, and nowhere a fixed-width header could match. Here every row is the same
 * CSS grid, header included, so the columns are aligned by construction rather than by agreement.
 * <c>LegOption</c> is untouched and still draws every vessel that has no classes.</p>
 *
 * <p>A cell is <b>an action, not a claim</b>: "put all of us in this class on this sailing". That is
 * what lets the card stay honest once the classes are mixed — the cells go on offering a change, and
 * the booking's own total is stated separately rather than one cell pretending to describe it.</p>
 *
 * <p><b>The row carries no tick.</b> Selection is drawn on the cell that was chosen, because on a
 * card with three prices "this sailing is selected" is the smaller half of the answer — the booker
 * also picked a class, and a tick in the margin cannot say which.</p>
 */
interface Props {
	trip: TicketsTabTrip;
	classPrices: TripClassPrice[];
	wizardData: BookingWizardData;
	saveChanges: (next: BookingWizardData) => void;
	legType: 'departure' | 'return';
	isSelected: boolean;
	/**
	 * The booking's spread of fare types — `2 Comfort · 1 Premium` — where it has more than one.
	 *
	 * <p>Passed in rather than derived here so every card and the header above them print the same
	 * line, and so the question is asked of the booking once per day rather than once per sailing.</p>
	 */
	mixLabel?: string | null;
	/**
	 * What taking this sailing, with the booking exactly as it stands, would change the bill by.
	 *
	 * <p>Server-priced against the booking being edited — the same figure the plain trip card has
	 * always shown — and null outside an alteration, where there is nothing to change and the fare
	 * itself is what the booker is choosing between.</p>
	 *
	 * <p>It covers the sailing, not the fare type: moving the party into a different fare type on top
	 * of it is a passenger-fare difference, which the cells add themselves from the same per-head
	 * fares they print. That keeps one request per window instead of one per fare type per sailing,
	 * and the two halves cannot drift because the server prices the booking as it is.</p>
	 */
	alterationDelta?: number | null;
	/** Re-fetches the window after a waitlist join, so the row can stop offering it. */
	refreshTrips?: () => void;
}

/** A departure or arrival time, with AM / PM set small and grey so a 12-hour time is barely wider than a 24-hour one. */
function CardTime({ date }: { date?: Date | string }) {
	const { time, period } = formatStandardTimeParts(date);
	return (
		<span className="fare-card__time">
			{time}
			{period && <span className="fare-card__time-period">{period}</span>}
		</span>
	);
}

export default function FareTripCard({
	trip,
	classPrices,
	wizardData,
	saveChanges,
	legType,
	isSelected,
	mixLabel,
	alterationDelta,
	refreshTrips,
}: Props) {
	const store = useStore();
	const { formatMessage } = useIntl();
	const counts = React.useMemo(() => passengerCountsByType(wizardData), [wizardData]);

	/**
	 * Whether this sailing can be taken at all, and why not when it cannot.
	 *
	 * <p>The same two hooks the plain card has always used. This card was built without them and
	 * passed `true` for selectable — so a returning sailing that leaves before the departing one
	 * took the click, said nothing, and left the booker looking at a card that had not changed. The
	 * rule was never gone; only the card that stated it.</p>
	 *
	 * <p>A selected sailing stays clickable whatever the answer: the click that gets OUT of a
	 * wrong choice is the same control as the one that made it.</p>
	 */
	const isSelectable = useIsLegSelectable(trip, wizardData, legType);
	const unavailableReason = useLegErrorMessage(wizardData, trip, legType);
	// The vessel takes no walk-ons. Not an `unavailableReason` — see `isWalkOnNotOffered` — so it has
	// a notice of its own below, read by staff too, who may still book it.
	const walkOnNotOffered = isWalkOnNotOffered(trip, wizardData.tabSelected);
	// Before the departing leg, after the returning one, or the other leg itself — a sailing no one
	// can take with this booking, whatever the fares say. See `fare-card--clash`.
	const clashes = !isSelected && legClash(trip, wizardData, legType) !== null;
	// Departed, folded on a phone the same way. Staff and managers can still book it, so for them the
	// fold opens — a card nobody could unfold would be a sailing they could only take from a laptop.
	const departed = !isSelected && trip.departed;
	const [departedOpen, setDepartedOpen] = React.useState(false);
	const selectTrip = useLegOptionClick(legType, wizardData, trip, isSelectable || isSelected, saveChanges);
	const chosen = selectedClassId(wizardData, legType);
	const partySize = Object.values(counts).reduce((total, count) => total + count, 0);
	/*
		A party split across fare types, on every card rather than only the one they are on.

		This used to be `isSelected && …`, which made it true almost nowhere: the step collapses to
		`SelectedFareTrip` the moment a sailing is chosen, so the cards are on screen exactly when
		none is selected. A booking sold across two fare types — which is most bookings that reach
		this step by being edited — was therefore offered a grid of cells that each quietly flatten
		the party onto one class, with nothing saying so and no way to move sailing without it.
	*/
	const mixed = chosen === 'MIXED';

	/**
	 * What the party costs here with everybody left in the fare type they already hold.
	 *
	 * <p>Asked whether or not the party is split: for a split one it is what keeping the mix costs,
	 * and for any booking being edited it is the baseline every other cell's difference is measured
	 * from. Null where this sailing does not sell one of the fare types in play — a different vessel,
	 * a fare type this route does not carry — and the card can then neither offer to keep the split
	 * nor say what a move would cost against it.</p>
	 */
	const currentTotal = React.useMemo(
		() => splitPartyTotal(classPrices, passengerFaresByLeg(wizardData, legType)),
		[classPrices, wizardData, legType],
	);
	const splitTotal = mixed ? currentTotal : null;

	/**
	 * What each cell would change the bill by, or null where this step is not editing a booking.
	 *
	 * <p>The sailing's own difference plus what moving the party into that fare type adds or saves.
	 * Both halves are needed: the first covers the change of time and anything the server charges for
	 * making it, the second covers the fares, and neither on its own is what the customer would pay.</p>
	 */
	const changeFor = (classTotal: number): number | null =>
		alterationDelta != null && currentTotal != null ? alterationDelta + classTotal - currentTotal : null;

	// Whoever may bypass the capacity check can overbook a fare type — the reservation step sends them
	// with the check bypassed — so to them a full sailing is not gone: its cells stay priced and choosable.
	const gone =
		!store.can('OverbookInBookingWizard') &&
		sailingIsGone(
			trip.capacityTags,
			classPrices.map(x => x.passengerClassId),
			partySize,
		);

	// The same rule the fare cells lock on, so the keep-as-they-are column cannot be offered on a
	// sailing a click would be refused on.
	const canSelect = isSelectable || store.can('OverrideBookingRestrictions');

	/**
	 * Whether this sailing gets a "keep the mix" column of its own.
	 *
	 * <p>Only where the party is split, only where the sailing can actually take them as they are,
	 * and not on the one they are already on — there the mix is not a choice, it is the state, and
	 * the note under the card says where to change it.</p>
	 *
	 * <p>The column count below follows this, so the header and the cards cannot disagree about how
	 * many columns the row has: both are told by <c>FilteredTripCards</c>, which asks the booking
	 * once.</p>
	 */
	const showMix = mixed && !isSelected && !gone && splitTotal !== null;

	/**
	 * Whether the header put a `Keep current mix` column above this row.
	 *
	 * <p>Not the same question as `showMix`. The header counts its columns once for the whole
	 * window, from the booking — a split party gets the column on every sailing — while this card
	 * decides per sailing whether it can actually offer the mix. On a sailing that does not sell one
	 * of the fare types on the booking the two disagreed, the card came out a column short, and
	 * every price in the row sat under the wrong fare type with the last one under no heading at
	 * all. The column is held either way; only what goes in it changes.</p>
	 */
	const mixColumn = !!mixLabel;

	// What the chosen class offers, in the operator's words. Shown only once it is chosen: three
	// descriptions stacked under an unselected row is a paragraph where a price list should be.
	const chosenDescription =
		isSelected && chosen && chosen !== 'MIXED'
			? describeClassPoints(classPrices.find(x => x.passengerClassId === chosen)?.description).join(' · ')
			: undefined;

	const choose = (passengerClassId: string) => {
		// This leg's fare type only. The other leg is a separate sailing with its own choice, and
		// writing both from one card is what let a returning selection change the departing one.
		//
		// The seat is left alone. This card cannot tell whether it survives — that depends on the
		// sailing's own seating, which only the seating step holds — and clearing it here threw away
		// a seat every time the booker re-picked the fare type they were already in. The seating step
		// drops it if the trip no longer offers it.
		const patched = patchAllPassengers(wizardData, () =>
			passengerClassPatch(
				fareFieldLeg(wizardData, legType),
				passengerClassId,
				classPrices.find(x => x.passengerClassId === passengerClassId)?.name,
			),
		);

		if (isSelected) {
			saveChanges(patched);
			return;
		}

		// One save, not two. `selectTrip` closes over the wizard data of the render that made it, so
		// saving the class first and selecting after would select on top of data taken before the
		// class existed — which is how every newly-selected sailing came back with no class on it.
		selectTrip(patched);
	};

	/**
	 * Takes this sailing and leaves everybody in the fare type they are already in.
	 *
	 * <p>The only way a split party can change sailing without being flattened. The fare cells cannot
	 * do it — a cell names one class, and clicking it means "put everyone in this" — so a booking
	 * with one passenger upgraded had to be re-split on the seating step after every change of
	 * time.</p>
	 *
	 * <p>The seats are carried across rather than dropped. They belong to the sailing being left, on
	 * a vessel this one may not even share — but whether a given seat still exists, is still free and
	 * is still sold in its passenger's fare type is a question only the new sailing's seating can
	 * answer, so the seating step is the one that answers it.</p>
	 */
	const keepFareTypes = () => {
		selectTrip(wizardData);
	};

	return (
		<div
			className={classNames('fare-card', {
				'fare-card--selected': isSelected,
				'fare-card--gone': gone,
				// A sailing the rules refuse — before the departing leg, after the returning one,
				// the other leg itself. Drawn as refused rather than merely captioned: a card that
				// looks live and answers with a sentence is one the booker clicks twice.
				'fare-card--unavailable': !isSelectable && !isSelected,
				// Folded to its times and the reason on a phone, where every fare type is a
				// full-width row: a sailing the other leg rules out was three rows of prices that
				// could not be tapped, pushing the ones that could off the screen.
				'fare-card--clash': clashes,
				'fare-card--folded': departed && !departedOpen,
				'fare-card--twelve-hour': usesTwelveHourClock(),
			})}
			// The grid's column count comes from the data, so the header and the card cannot
			// disagree about how many there are.
			style={{ '--fare-columns': classPrices.length + (mixColumn ? 1 : 0) } as React.CSSProperties}
		>
			<div className="fare-card__rail" aria-hidden="true" />

			<div className="fare-card__journey">
				<span className="fare-card__times">
					<CardTime date={trip.departureDateTime} />
					{/*
						The vessel leaving and the place it arrives, the same two icons the trip cards
						have always put either side of the dotted run. They were dropped to two plain
						dots when the table was drawn, which left the line reading as a range between two
						numbers rather than as a crossing.
					*/}
					<span className="fare-card__track" aria-hidden="true">
						<Icon name="ship" />
						<em />
						<Icon name="location-pin" />
					</span>
					<CardTime date={trip.arrivalDateTime} />
				</span>

				{/*
					Why this sailing cannot be taken — that it is before the departing leg, after the
					returning one, already the other leg, departed or closed. Not for a sold-out
					sailing: the cells say `Sold out` themselves and the footer offers the waitlist, so
					a third telling is noise.

					Under the times rather than across the foot of the card. As its own band it was a
					row of its own, which made a refused sailing the tallest thing in the table and put
					a filled strip in the gap between two rows, where it read as a heading for the one
					below. The times take one line in a column sized by the fare cells beside it, so
					there is already room under them and the card does not grow.
				*/}
				{!isSelectable && !isSelected && !gone && unavailableReason && (
					<span className="fare-card__notice">
						<Icon name="info" useMaterialIcons classname="fare-card__notice-mark" />
						<span>{unavailableReason}</span>
					</span>
				)}

				{walkOnNotOffered && !isSelected && (
					<span className="fare-card__notice">
						<Icon name="info" useMaterialIcons classname="fare-card__notice-mark" />
						<span>{formatMessage(walkOnNotOfferedMessage)}</span>
					</span>
				)}

				{/*
					Departed, said to staff and managers too though they can still take it — a sailing that
					has left looked exactly like one about to. On a phone the fold's control below says it
					instead, so this stands down there. Customers get the reason above.
				*/}
				{departed && isSelectable && (
					<span className="fare-card__notice fare-card__notice--folds">
						<Icon name="info" useMaterialIcons classname="fare-card__notice-mark" />
						<span>
							{formatMessage({
								id: 'general__departed__label',
								defaultMessage: 'Departed',
							})?.toUpperCase()}
						</span>
					</span>
				)}

				{/* Phone only — the desktop columns never fold. Shown to whoever may still book it. */}
				{departed && isSelectable && (
					<button
						type="button"
						className="fare-card__fold-toggle"
						aria-expanded={departedOpen}
						onClick={() => setDepartedOpen(open => !open)}
					>
						{departedOpen ? 'Departed · Hide fares' : 'Departed · Show fares'}
					</button>
				)}
			</div>

			{/*
				Keeping the party as it is, priced like any other fare.

				It sits in the row rather than under it because it competes with the columns beside
				it: the booker is choosing between "everyone Comfort", "everyone Premium" and "leave
				them as they are", and only one of those three was previously a thing they could
				click. Its figure is what the sailing costs at the fare types they already hold, so
				the captions on the cells to its right can be read against it.
			*/}
			{/*
				The reserved column, held on a sailing that cannot offer the mix.

				Empty on a sold-out sailing and on the one the booking is already on, where the cells
				beside it say `Sold out` and the note under the card already explains the split. On a
				sailing that simply does not sell one of the fare types it carries the reason, which
				is where that reason belongs — in the column it is about, rather than in a banner
				under the card saying it once per sailing.
			*/}
			{mixColumn && !showMix && (
				<div className="fare-card__cell fare-card__cell--mix fare-card__cell--mix-off">
					{!gone && !isSelected && canSelect && splitTotal === null && (
						<span className="fare-card__cell-sub">Not on this sailing</span>
					)}
				</div>
			)}

			{showMix && (
				<div
					role="button"
					tabIndex={0}
					className="fare-card__cell fare-card__cell--mix"
					onKeyDown={event => {
						if (event.key !== 'Enter' && event.key !== ' ') {
							return;
						}
						event.preventDefault();
						keepFareTypes();
					}}
					onClick={keepFareTypes}
				>
					<span className="fare-card__cell-main">
						<span className="fare-card__cell-label">Keep current mix</span>
						{/*
							The mix itself on a phone, where there is no column heading above to
							carry it, and "no fare changes" on the desktop columns, where the heading
							has already said what the mix is.
						*/}
						<span className="fare-card__cell-sub">
							<span className="fare-card__cell-only">no fare changes</span>
							<span className="fare-card__cell-also fare-card__cell-also--solo">{mixLabel}</span>
						</span>
					</span>
					<span className="fare-card__cell-figure">
						{/*
							No caption on an edit. `+$20.00` is already a change, and a word over it
							saying so was the same fact in two places — where `From` and `Total`
							earn theirs by telling the booker what kind of figure follows.
						*/}
						{alterationDelta == null && <span className="fare-card__from">Total</span>}
						<span className="fare-card__price">
							{alterationDelta != null
								? formatFareChange(alterationDelta)
								: formatPriceDisplay(splitTotal ?? 0, false)}
						</span>
					</span>
					<span className="fare-card__cell-go" aria-hidden="true">
						›
					</span>
				</div>
			)}

			{classPrices.map((classPrice, index) => {
				const isChosen = isSelected && chosen === classPrice.passengerClassId;
				const tag = classCapacityTag(trip.capacityTags, classPrice.passengerClassId);
				const availability = fareAvailability(tag, partySize);
				const bookable = availability !== 'SOLD_OUT' && availability !== 'NOT_ENOUGH';
				// A full fare type someone who may overbook is choosing anyway. Priced and choosable, and
				// marked so the overbooking is a decision rather than an accident.
				const overbooking = !bookable && store.can('OverbookInBookingWizard');
				const seatsLeft = capacityLabel(tag, availability);

				/**
				 * What moving everyone into this fare type would do to the price, against keeping
				 * the mix.
				 *
				 * <p>Only while the mix column is beside it — the two figures only mean anything as
				 * a pair, and against a party that is all in one fare type the comparison is with
				 * what they are paying now, which this step cannot see. Null where it would be zero:
				 * a fare type that costs the same is better described by how many seats it has
				 * left.</p>
				 */
				const classTotal = classPartyTotal(classPrice, counts);
				// Suppressed on an edit: the figure on the right is already the difference, and
				// saying "saves $20.00" beside "-$20.00" is the same fact twice.
				const againstMix =
					showMix && splitTotal !== null && alterationDelta == null ? classTotal - splitTotal : 0;

				// What this cell would do to the bill, on an edit. Null while creating a booking.
				const change = changeFor(classTotal);
				// An edit whose current mix this sailing cannot price — one of the booking's fare types
				// is not sold here — has no difference to show, and a full total under a `Price change`
				// heading reads as the bill. So the cell is not offered at all rather than priced wrong.
				const unpriced = alterationDelta != null && change === null;

				const locked =
					(!bookable && !overbooking) || unpriced || (!isSelectable && !isSelected && !store.can('OverrideBookingRestrictions'));

				return (
					/*
						A row, not a button — and deliberately so.

						The fare type's ⓘ is a control of its own, and the app's popover renders a
						real `<button>` for its trigger. A button inside a button is not merely
						invalid: the HTML parser closes the outer one where the inner one starts, so
						the row would come apart in the DOM. The row therefore carries the button
						role itself, which lets a second control live inside it — and keeps the
						click, the keyboard and what a screen reader announces exactly as they were.
					*/
					<div
						key={classPrice.passengerClassId}
						role="button"
						tabIndex={locked ? -1 : 0}
						aria-disabled={locked}
						onKeyDown={event => {
							if (locked || (event.key !== 'Enter' && event.key !== ' ')) {
								return;
							}
							event.preventDefault();
							choose(classPrice.passengerClassId);
						}}
						className={classNames('fare-card__cell', {
							'fare-card__cell--on': isChosen,
							'fare-card__cell--low': availability === 'LOW' || availability === 'NOT_ENOUGH',
							'fare-card__cell--gone': (!bookable && !overbooking) || unpriced,
							// Only where one of them would otherwise be drawn as chosen. On a card the
							// booker has not taken there is nothing to un-choose, and dimming every cell of
							// every sailing reads as a list that cannot be used at all.
							'fare-card__cell--dim': mixed && isSelected,
						})}
						onClick={() => !locked && choose(classPrice.passengerClassId)}
					>
						{/*
							`__cell-main` is `display: contents` on a desktop column, so its children
							join the cell's own stack and the layout is the one the columns need. At
							phone width it becomes the left half of a full-width row, which is the
							only reason it exists.
						*/}
						<span className="fare-card__cell-main">
							{/*
								The class's name and colour, carried by every cell and shown only
								where there is no column heading to carry them — which is to say, on
								a phone.
							*/}
							<span className="fare-card__cell-label">
								{/*
									Two fills, one element. The desktop columns carry the class's own
									colour in their headings, and a phone has no headings — so at that
									size the dot is spent on availability instead. Which one is used is
									a stylesheet decision, so the breakpoint stays in the stylesheet.
								*/}
								<span
									className="fare-card__swatch"
									style={
										{
											'--fare-swatch-class': classColour(index, classPrice.colour),
											'--fare-swatch-availability': availabilitySwatch(availability),
											background: 'var(--fare-swatch, var(--fare-swatch-class))',
										} as React.CSSProperties
									}
								/>
								{/*
									`All Comfort`, not `Comfort`, once the party is split: on a phone
									the row IS the button, and what it does is move everybody into
									that fare type. The desktop columns keep the bare name, because
									there the heading names the class and the cell is a price in it.
								*/}
								{mixed ? `All ${classPrice.name}` : classPrice.name}
								{/*
									What the class includes, on the row itself. The desktop columns
									carry one in their headings — said once for the whole day — and a
									phone has no headings, so without this the detail is reachable
									only after the class has been chosen. Hidden at desktop width by
									the stylesheet rather than by a second render path.
								*/}
								{/*
									The row is the target for everything except this. A tap on the ⓘ
									would otherwise run up to the row's own handler and choose the
									fare type — so the booker asking what Premium includes buys it
									instead. Keyboard too: the row answers Enter and Space, and so
									does the trigger inside it.
								*/}
								<span
									className="fare-card__cell-info"
									onClick={event => event.stopPropagation()}
									onKeyDown={event => event.stopPropagation()}
								>
									<ClassInfoPopover
										classPrice={classPrice}
										counts={counts}
										colour={classColour(index, classPrice.colour)}
									/>
								</span>
							</span>

							{isChosen && (
								<span className="fare-card__cell-sub fare-card__cell-sub--on">
									{/* The tick is the dot's job on a phone, where the dot is in view. */}
									<Icon name="tick" classname="fare-card__cell-tick" />
									Selected
									{seatsLeft && <span className="fare-card__cell-also">{`\u00b7 ${seatsLeft}`}</span>}
								</span>
							)}
							{/*
								The difference wins the line over the seats left when there is one.
								A booker with a split party is deciding what a move costs, and "252
								seats left" is not an answer to that; a fare type that is running out
								still says so, because that is the one case where the count changes
								the decision.
							*/}
							{/*
								What moving everyone here would cost, with how many seats it has
								left behind it.

								Both facts on a phone, where the row is the width of the screen;
								the difference alone in a desktop column, which is 120px wide and
								would wrap the pair onto three lines. `__cell-also` is the same
								element the selected row uses for exactly this, and the stylesheet
								already decides where it shows.
							*/}
							{/*
								What a manager is overbooking into, and whether the passengers over the
								line will have a seat of their own. Seats short sends them to the Seats
								step's "other fare types" plan, so it is said here, before they choose.
							*/}
							{!isChosen && overbooking && (
								<span className="fare-card__cell-sub fare-card__cell-sub--chip">
									{availability === 'SOLD_OUT' ? 'Sold out' : 'Not enough places'}
									{(() => {
										const free = classPrice.freeSeats;
										if (free == null || free >= partySize) {
											return ' · overbook';
										}
										return free === 0
											? ' · no seats left'
											: ` · only ${free} ${free === 1 ? 'seat' : 'seats'}`;
									})()}
								</span>
							)}
							{!isChosen && !overbooking && availability !== 'SOLD_OUT' && againstMix !== 0 && (
								<span
									className={classNames('fare-card__cell-sub', {
										'fare-card__cell-sub--saves': againstMix < 0,
									})}
								>
									{againstMix < 0
										? `Saves ${formatPriceDisplay(-againstMix, false)}`
										: `+${formatPriceDisplay(againstMix, false)}`}
									{seatsLeft && <span className="fare-card__cell-also">{`\u00b7 ${seatsLeft}`}</span>}
								</span>
							)}
							{!isChosen &&
								!overbooking &&
								availability !== 'SOLD_OUT' &&
								seatsLeft &&
								againstMix === 0 && (
									<span
										className={classNames('fare-card__cell-sub', {
											'fare-card__cell-sub--chip': availability !== 'AVAILABLE',
										})}
									>
										{seatsLeft}
									</span>
								)}
						</span>

						<span className="fare-card__cell-figure">
							{/* eslint-disable-next-line no-nested-ternary */}
							{availability === 'SOLD_OUT' && !overbooking ? (
								<span className="fare-card__price">Sold out</span>
							) : unpriced ? (
								<span className="fare-card__cell-sub">Not available on this sailing</span>
							) : (
								<>
									{/*
										Three different questions, three different figures.

										Editing a booking, the only one that matters is what it
										would change the bill by — the fare is already paid, and a
										signed figure says so without a caption over it. Booking
										fresh with the party split, the cell is a complete price for
										everyone in that fare type, so `Total`. Otherwise `From`.
									*/}
									{change === null && (
										<span className="fare-card__from">{mixed ? 'Total' : 'From'}</span>
									)}
									<span className="fare-card__price">
										{change !== null
											? formatFareChange(change)
											: formatPriceDisplay(classTotal, false)}
									</span>
								</>
							)}
						</span>

						{/*
							Tappable, and said so. A phone row is the width of the screen with nothing
							else on it, so the arrow is what tells a thumb the row is the target rather
							than the price inside it. Hidden on the desktop columns, which are plainly
							buttons already, and on rows that cannot be chosen.
						*/}
						<span className="fare-card__cell-go" aria-hidden="true">
							›
						</span>
					</div>
				);
			})}

			{/*
				A sailing with nothing left stops offering fares and offers the waitlist instead —
				the same button, modal and staff rule as a vessel with no classes, so joining a
				waitlist is one thing to learn rather than two.
			*/}
			{gone && !store.isStaff && !trip.departed && (
				<div className="fare-card__footer fare-card__footer--gone">
					<span className="fare-card__footer-label">All fare types sold out</span>
					<WaitlistButton
						isWaitlisted={trip.isWaitListed}
						trip={trip}
						wizardData={wizardData}
						refresh={refreshTrips}
					/>
				</div>
			)}

			{chosenDescription && (
				<div className="fare-card__footer">
					<span className="fare-card__footer-note">{chosenDescription}</span>
				</div>
			)}

			{/*
				A split party, and what this card can do about it.

				Two different situations under one heading. On the sailing they are on, the split is
				something to go and edit — the seating step is where a fare type belongs to a person
				rather than to the booking. On a sailing they are considering, it is a warning with a
				way past it: every cell here means "put everyone in this", so without the button a
				booker changing their time has no way to arrive with the party they left with.
			*/}
			{/*
				What a split party can do with this sailing.

				The sentence explaining the split is said once above the list, not here: a day has
				five sailings and five copies of one paragraph is a wall. What belongs on the card is
				the part that differs between sailings — the price of keeping the party as it is, and
				whether this vessel can take them that way at all.

				On the sailing they are already on there is nothing to select, so the card says where
				to go instead: a fare type belongs to a person, and the seating step is where it is
				edited per person.
			*/}
			{mixed && !gone && isSelected && (
				<div className="fare-card__mixed">
					{/*
						The same mark as the panel above the list, because it is the same sentence
						about the same booking — only said on the sailing they have settled on.
					*/}
					<span className="fare-card__mixed-mark" aria-hidden="true">
						i
					</span>
					<span className="fare-card__mixed-note">
						Your travellers are in different fare types. Change them individually at the seating step, or
						pick one above to move everyone.
					</span>
				</div>
			)}

			{/*
				The way back out. Selecting a class selects the sailing, so without this a booker who
				picked the wrong one has no way to un-pick it — the fare cells only ever move them
				between classes on the sailing they are already on.

				Same handler, wording and classes as LegOptionChangeSelection, because to a booker it
				is the same control: only the card around it changed.
			*/}
			{isSelected && (
				<div
					className={`fare-card__change ${
						legType === 'departure' ? 'departure-ticket' : 'return-ticket'
					} change-selected-ticket`}
				>
					<Button onClick={selectTrip}>
						Change {legType === 'departure' ? 'departing' : 'returning'} trip time
					</Button>
				</div>
			)}
		</div>
	);
}
